import React, { useState, useEffect, useRef } from 'react';
import { deviceAPI, nasAPI, planAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useBranchStore } from '../../store';
import usePermission from '../../hooks/usePermission';
import {
    Wifi, Laptop, Trash2, Plus, X, Shield, Ban, Clock, Tv, Camera,
    Speaker, Search, Upload, Download, FileText, CheckCircle, AlertCircle,
    Edit2, Power, RefreshCw, Filter, ChevronLeft, ChevronRight,
    User, Zap, Activity, Globe, MoreVertical
} from 'lucide-react';
import './Devices.css';
import DateInput from '../../components/DateInput/DateInput';
import { formatDate } from '../../utils/dateFormat';

/* ── CSV helpers ─────────────────────────────────────────────────────── */
// Accepts any common MAC format: colon, hyphen, dot, or no separator
const MAC_REGEX = /^([0-9A-Fa-f]{2}[:\-.]){5}[0-9A-Fa-f]{2}$|^[0-9A-Fa-f]{12}$/;
const VALID_TYPES = ['tv','camera','speaker','set_top_box','printer','iot','laptop','mobile','tablet','other'];
const VALID_STATUS = ['whitelisted','blacklisted','pending'];

function parseCSVLine(line) {
    const cols = []; let inQ = false; let cur = '';
    for (let i = 0; i < line.length; i++) {
        if (line[i] === '"') { inQ = !inQ; }
        else if (line[i] === ',' && !inQ) { cols.push(cur); cur = ''; }
        else { cur += line[i]; }
    }
    cols.push(cur);
    return cols.map(c => c.trim().replace(/^"|"$/g, ''));
}

function parseCSV(text) {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return { rows: [], error: 'CSV must have a header row and at least one data row.' };
    const hdr = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[" ]/g, ''));
    const macIdx      = hdr.indexOf('mac');
    const nameIdx     = hdr.indexOf('name');
    const typeIdx     = hdr.indexOf('type');
    const statusIdx   = hdr.indexOf('status');
    const bypassIdx   = hdr.findIndex(h => h === 'bypass_portal' || h === 'bypassportal');
    const notesIdx    = hdr.indexOf('notes');
    const planIdx     = hdr.indexOf('plan');
    const expiryIdx   = hdr.findIndex(h => h === 'expiry_date' || h === 'expirydate' || h === 'expiry');
    const vlanIdx     = hdr.findIndex(h => h === 'vlan_id' || h === 'vlanid' || h === 'vlan');
    const nasIdx      = hdr.findIndex(h => h === 'nas' || h === 'router' || h === 'nasname');
    const ipIdx       = hdr.findIndex(h => h === 'ip_address' || h === 'ipaddress' || h === 'ip');
    const dhcpIdx     = hdr.findIndex(h => h === 'dhcp_type' || h === 'dhcptype' || h === 'dhcp');
    if (macIdx === -1) return { rows: [], error: 'CSV must have a "mac" column.' };
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim(); if (!line) continue;
        const cols    = parseCSVLine(line);
        const mac     = (cols[macIdx]    || '').toUpperCase();
        const name    = cols[nameIdx]   || '';
        const type    = (cols[typeIdx]   || 'other').toLowerCase();
        const status  = (cols[statusIdx] || 'whitelisted').toLowerCase();
        const bypass  = bypassIdx !== -1 ? (cols[bypassIdx]?.toLowerCase() === 'true' || cols[bypassIdx] === '1') : false;
        const notes   = notesIdx  !== -1 ? cols[notesIdx] || '' : '';
        const planName = planIdx  !== -1 ? (cols[planIdx] || '').trim() : '';
        const expiryDate = expiryIdx !== -1 ? (cols[expiryIdx] || '').trim() : '';
        const vlanId  = vlanIdx !== -1 ? (cols[vlanIdx] || '').trim() : '';
        const nasName = nasIdx  !== -1 ? (cols[nasIdx]  || '').trim() : '';
        const ipAddress = ipIdx   !== -1 ? (cols[ipIdx]   || '').trim() : '';
        const dhcpType  = dhcpIdx !== -1 ? (cols[dhcpIdx] || '').trim() : '';
        const errs = [];
        if (!mac || !MAC_REGEX.test(mac)) errs.push('Invalid MAC');
        if (!name)                        errs.push('Name required');
        if (type && !VALID_TYPES.includes(type))     errs.push(`Unknown type "${type}"`);
        if (status && !VALID_STATUS.includes(status)) errs.push(`Unknown status "${status}"`);
        if (expiryDate && isNaN(Date.parse(expiryDate))) errs.push('Invalid expiry date');
        rows.push({
            mac, name,
            type:        VALID_TYPES.includes(type) ? type : 'other',
            status:      VALID_STATUS.includes(status) ? status : 'whitelisted',
            bypassPortal: bypass, notes, planName,
            expiryDate, vlanId, nasName, ipAddress, dhcpType,
            _row: i, _errors: errs, _valid: errs.length === 0,
        });
    }
    return { rows, error: null };
}

function generateCSV(devices) {
    const hdr = 'mac,name,type,status,bypass_portal,plan,expiry_date,vlan_id,nas,ip_address,dhcp_type,notes';
    const q = s => `"${(s||'').replace(/"/g,'""')}"`;
    const fmtDate = d => d ? new Date(d).toISOString().slice(0,10) : '';
    return [hdr, ...devices.map(d => [
        d.mac,
        q(d.name||''),
        d.type||'other',
        d.status||'whitelisted',
        d.bypassPortal ? 'true' : 'false',
        q(d.planId?.name||''),
        fmtDate(d.expiryDate),
        d.vlanId||'',
        q(d.nasId?.name||''),
        d.ipAddress||'',
        d.dhcpType||'',
        q(d.notes||''),
    ].join(','))].join('\n');
}

const CSV_TEMPLATE = `mac,name,type,status,bypass_portal,plan,expiry_date,vlan_id,nas,ip_address,dhcp_type,notes
AA:BB:CC:DD:EE:11,Lobby TV,tv,whitelisted,true,20MBPS,2026-12-31,,Main Router,,dhcp_auth,Reception Area
AA:BB:CC:DD:EE:22,Security Camera,camera,whitelisted,true,15MBPS,,,Any NAS,172.20.6.88,dhcp_auth,Entrance`;

function triggerDownload(content, filename, mime='text/csv') {
    const blob = new Blob([content], { type: mime });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}

function fmtBytes(b) {
    if (!b) return '—';
    if (b >= 1073741824) return `${(b/1073741824).toFixed(1)} GB`;
    if (b >= 1048576)    return `${(b/1048576).toFixed(1)} MB`;
    if (b >= 1024)       return `${(b/1024).toFixed(1)} KB`;
    return `${b} B`;
}

function timeAgo(date) {
    if (!date) return '—';
    const diff = Date.now() - new Date(date).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1)   return 'just now';
    if (m < 60)  return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)  return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

function isExpired(d) {
    return d.expiryDate && new Date(d.expiryDate) < new Date();
}

const TYPE_OPTIONS = [
    { value: 'tv',          label: 'TV',          icon: Tv },
    { value: 'camera',      label: 'Camera',      icon: Camera },
    { value: 'speaker',     label: 'Speaker',     icon: Speaker },
    { value: 'set_top_box', label: 'Set-Top Box', icon: Tv },
    { value: 'printer',     label: 'Printer',     icon: Laptop },
    { value: 'iot',         label: 'IoT Device',  icon: Wifi },
    { value: 'laptop',      label: 'Laptop',      icon: Laptop },
    { value: 'mobile',      label: 'Mobile',      icon: Wifi },
    { value: 'tablet',      label: 'Tablet',      icon: Wifi },
    { value: 'other',       label: 'Other',       icon: Wifi },
];

const DEFAULT_FORM = {
    mac: '', name: '', type: 'other', status: 'whitelisted',
    bypassPortal: false, planId: '', notes: '',
    expiryDate: '', nasId: '', vlanId: '', assignedUserId: '',
    ipAddress: '', dhcpType: 'dhcp_auth'
};

const LIMIT = 20;

/* ── DeviceForm — defined OUTSIDE Devices so it is never recreated on
   parent re-render (defining it inside caused focus loss on every keystroke) */
const DeviceForm = ({ onSubmit, submitLabel, formData, setFormData, editDevice, plans, nasList, onCancel }) => {
    /* Use functional setter everywhere so stale-closure never overwrites a field */
    const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));
    const setCheck = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.checked }));

    /* Stable ref so requestAnimationFrame cursor-restore always targets the live DOM node */
    const macInputRef = React.useRef(null);

    /* MAC: strip any separator, uppercase, auto-format to XX:XX:XX:XX:XX:XX */
    const onMacChange = (e) => {
        const raw       = e.target.value.replace(/[^0-9A-Fa-f]/g, '').toUpperCase().slice(0, 12);
        const formatted = raw.match(/.{1,2}/g)?.join(':') || raw;
        setFormData(prev => ({ ...prev, mac: formatted }));
    };

    return (
        <form onSubmit={onSubmit}>
            <div className="modal-body-modern">
                <div className="form-grid-2col">
                    {/* MAC */}
                    <div className="form-group-modern full-width">
                        <label>MAC Address *</label>
                        <input ref={macInputRef} type="text" className="mac-input"
                            placeholder="AA:BB:CC:DD:EE:FF"
                            value={formData.mac}
                            onChange={onMacChange}
                            required readOnly={!!editDevice}
                            pattern="^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$"
                            title="Valid MAC address — any format accepted (e.g. 60:32:B1:FF:30:6B)"
                        />
                    </div>
                    {/* Name */}
                    <div className="form-group-modern">
                        <label>Device Name *</label>
                        <input type="text" placeholder="e.g. Lobby TV"
                            value={formData.name}
                            onChange={set('name')}
                            required />
                    </div>
                    {/* Type */}
                    <div className="form-group-modern">
                        <label>Device Type</label>
                        <select value={formData.type} onChange={set('type')}>
                            {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                    {/* Status */}
                    <div className="form-group-modern">
                        <label>Access Control *</label>
                        <select value={formData.status} onChange={set('status')}>
                            <option value="whitelisted">✓ Whitelisted (Allow)</option>
                            <option value="blacklisted">✗ Blacklisted (Block)</option>
                            <option value="pending">⏳ Pending Review</option>
                        </select>
                    </div>
                    {/* Plan */}
                    <div className="form-group-modern">
                        <label>Internet Plan</label>
                        <select value={formData.planId} onChange={set('planId')}>
                            <option value="">None — NAS default</option>
                            {plans.map(p => (
                                <option key={p._id} value={p._id}>
                                    {p.name} ({p.bandwidth?.downloadSpeed || '—'}/{p.bandwidth?.uploadSpeed || '—'} Kbps)
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Expiry */}
                    <div className="form-group-modern">
                        <label>Expiry Date <span className="field-opt">(optional)</span></label>
                        <DateInput type="date" value={formData.expiryDate} onChange={set('expiryDate')} />
                    </div>
                    {/* NAS */}
                    <div className="form-group-modern">
                        <label>NAS / Router <span className="field-opt">(optional)</span></label>
                        <select value={formData.nasId} onChange={set('nasId')}>
                            <option value="">Any NAS</option>
                            {nasList.map(n => <option key={n._id} value={n._id}>{n.name} ({n.ipAddress || 'dynamic'})</option>)}
                        </select>
                    </div>
                    {/* VLAN */}
                    <div className="form-group-modern">
                        <label>VLAN ID <span className="field-opt">(optional)</span></label>
                        <input type="number" placeholder="e.g. 10" min="1" max="4094"
                            value={formData.vlanId} onChange={set('vlanId')} />
                    </div>
                    {/* Bypass portal */}
                    {formData.status === 'whitelisted' && (
                        <div className="form-group-modern full-width">
                            <label className="checkbox-wrapper">
                                <input type="checkbox" checked={formData.bypassPortal}
                                    onChange={setCheck('bypassPortal')} />
                                <span>Bypass Captive Portal (auto-login via MAC auth)</span>
                            </label>
                            <p className="input-hint">For TVs, cameras, and devices that cannot complete portal login.</p>
                            {formData.bypassPortal && !formData.planId && (
                                <p className="input-hint" style={{ color: '#f59e0b', marginTop: 4 }}>
                                    ⚠️ No plan assigned — this device will get <strong>unrestricted internet access</strong>. Assign a plan above to apply bandwidth limits.
                                </p>
                            )}
                            {formData.bypassPortal && formData.planId && (
                                <p className="input-hint" style={{ color: '#10b981', marginTop: 4 }}>
                                    ✓ Device will bypass the portal and authenticate via MAC. The selected plan's bandwidth limits will apply.
                                </p>
                            )}
                        </div>
                    )}
                    {/* Static IP / DHCP — only meaningful for bypass devices with a fixed lease */}
                    {formData.status === 'whitelisted' && formData.bypassPortal && (
                        <>
                            <div className="form-group-modern">
                                <label>Reserved IP <span className="field-opt">(optional)</span></label>
                                <input type="text" placeholder="e.g. 172.20.6.88"
                                    value={formData.ipAddress} onChange={set('ipAddress')} />
                            </div>
                            <div className="form-group-modern">
                                <label>DHCP Type</label>
                                <input type="text" placeholder="dhcp_auth"
                                    value={formData.dhcpType} onChange={set('dhcpType')} />
                            </div>
                        </>
                    )}
                    {/* Notes */}
                    <div className="form-group-modern full-width">
                        <label>Notes <span className="field-opt">(optional)</span></label>
                        <textarea rows="2" placeholder="Location, owner, or any notes…"
                            value={formData.notes} onChange={set('notes')} />
                    </div>
                </div>
            </div>
            <div className="modal-footer-modern">
                <button type="button" className="btn-secondary-modern" onClick={onCancel}>Cancel</button>
                <button type="submit" className="btn-primary-modern">
                    {editDevice ? <Edit2 size={16} /> : <Plus size={16} />}
                    {submitLabel}
                </button>
            </div>
        </form>
    );
};

/* ═══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════ */
const Devices = () => {
    const selectedBranch = useBranchStore(s => s.selectedBranch);
    const { canCreate, canEdit, canDelete } = usePermission('devices');
    const canManage = canCreate || canEdit || canDelete;

    /* data */
    const [devices,     setDevices]     = useState([]);
    const [stats,       setStats]       = useState(null);
    const [plans,       setPlans]       = useState([]);
    const [nasList,     setNasList]     = useState([]);
    const [suggestions, setSuggestions] = useState([]);

    /* loading */
    const [loading,      setLoading]      = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);

    /* pagination */
    const [page,       setPage]       = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total,      setTotal]      = useState(0);

    /* filters */
    const [searchTerm,         setSearchTerm]         = useState('');
    const [filterStatus,       setFilterStatus]       = useState('all');
    const [filterSessionStatus,setFilterSessionStatus]= useState('all');
    const [filterPlanId,       setFilterPlanId]       = useState('');
    const [filterBypass,       setFilterBypass]       = useState('all');
    const [filterExpired,      setFilterExpired]      = useState('all');
    const [showFilters,        setShowFilters]        = useState(false);

    /* search debounce */
    const searchRef = useRef('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
        return () => clearTimeout(t);
    }, [searchTerm]);

    /* selection */
    const [selectedIds,   setSelectedIds]   = useState(new Set());
    const [bulkWorking,   setBulkWorking]   = useState(false);
    const [showBulkMenu,  setShowBulkMenu]  = useState(false);
    const bulkMenuRef = useRef(null);

    /* modals */
    const [isAddOpen,      setIsAddOpen]      = useState(false);
    const [isEditOpen,     setIsEditOpen]     = useState(false);
    const [editDevice,     setEditDevice]     = useState(null);
    const [formData,       setFormData]       = useState(DEFAULT_FORM);

    /* assign-plan modal for bulk action */
    const [showPlanModal,  setShowPlanModal]  = useState(false);
    const [bulkPlanId,     setBulkPlanId]     = useState('');

    /* per-device action state */
    const [disconnecting,  setDisconnecting]  = useState({});
    const [syncing,        setSyncing]        = useState({});

    /* import */
    const [showImportModal, setShowImportModal] = useState(false);
    const [importStep,      setImportStep]      = useState(1);
    const [importRows,      setImportRows]      = useState([]);
    const [importResult,    setImportResult]    = useState(null);
    const [importing,       setImporting]       = useState(false);
    const [dragOver,        setDragOver]        = useState(false);
    const [csvError,        setCsvError]        = useState('');
    const fileInputRef = useRef(null);

    /* discover */
    const [showDiscoverModal, setShowDiscoverModal] = useState(false);
    const [discoverNasId,     setDiscoverNasId]     = useState('');
    const [discovering,       setDiscovering]       = useState(false);
    const [discovered,        setDiscovered]        = useState(null);

    /* ── Close bulk menu on outside click ─────────────────────────── */
    useEffect(() => {
        const handler = e => { if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) setShowBulkMenu(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    /* ── Reset page when filters change ───────────────────────────── */
    useEffect(() => { setPage(1); }, [debouncedSearch, filterStatus, filterSessionStatus, filterPlanId, filterBypass, filterExpired]);

    /* ── Fetch on branch / filter / page change ────────────────────── */
    useEffect(() => {
        fetchDevices();
        fetchStats();
    }, [selectedBranch, page, debouncedSearch, filterStatus, filterSessionStatus, filterPlanId, filterBypass, filterExpired]);

    useEffect(() => {
        fetchPlans();
        fetchNasList();
        fetchSuggestions();
    }, [selectedBranch]);

    /* ── Auto-refresh every 30s — keeps Online/Offline badges current ── */
    useEffect(() => {
        const timer = setInterval(() => {
            fetchDevices();
            fetchStats();
        }, 30000);
        return () => clearInterval(timer);
    }, [selectedBranch, page, debouncedSearch, filterStatus, filterSessionStatus, filterPlanId, filterBypass, filterExpired]);

    /* ── Data fetchers ────────────────────────────────────────────── */
    const fetchDevices = async () => {
        setLoading(true);
        try {
            const params = { branchId: selectedBranch?._id, page, limit: LIMIT };
            if (debouncedSearch)      params.search        = debouncedSearch;
            if (filterStatus !== 'all')        params.status        = filterStatus;
            if (filterSessionStatus !== 'all') params.sessionStatus = filterSessionStatus;
            if (filterPlanId)         params.planId        = filterPlanId;
            if (filterBypass !== 'all')        params.bypassPortal  = filterBypass;
            if (filterExpired !== 'all')       params.expired       = filterExpired;

            const res = await deviceAPI.getAll(params);
            setDevices(res.data?.data || []);
            const pg = res.data?.pagination;
            if (pg) { setTotalPages(pg.pages); setTotal(pg.total); }
        } catch (error) {
            console.error('Error fetching devices:', error);
            setDevices([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        setStatsLoading(true);
        try {
            const res = await deviceAPI.getStats({ branchId: selectedBranch?._id });
            setStats(res.data?.data);
        } catch { /* non-fatal */ }
        finally { setStatsLoading(false); }
    };

    const fetchPlans = async () => {
        try {
            const res = await planAPI.getAll(selectedBranch?._id);
            setPlans(res.data.data.plans || []);
        } catch { }
    };

    const fetchNasList = async () => {
        try {
            const res = await nasAPI.getAll({ branchId: selectedBranch?._id });
            setNasList(res.data?.data || []);
        } catch { }
    };

    const fetchSuggestions = async () => {
        try {
            const res = await deviceAPI.getSuggestions({ branchId: selectedBranch?._id });
            setSuggestions(res.data?.data || []);
        } catch { }
    };

    const refreshAll = () => { fetchDevices(); fetchStats(); fetchSuggestions(); };

    /* ── Add / Edit ────────────────────────────────────────────────── */
    const openAdd = (prefillMac = '') => {
        setFormData({ ...DEFAULT_FORM, mac: prefillMac });
        setIsAddOpen(true);
    };

    const openEdit = (device) => {
        setEditDevice(device);
        setFormData({
            mac:            device.mac,
            name:           device.name,
            type:           device.type,
            status:         device.status,
            bypassPortal:   device.bypassPortal,
            planId:         device.planId?._id || '',
            notes:          device.notes || '',
            expiryDate:     device.expiryDate ? device.expiryDate.split('T')[0] : '',
            nasId:          device.nasId?._id || '',
            vlanId:         device.vlanId || '',
            assignedUserId: device.assignedUserId?._id || '',
            ipAddress:      device.ipAddress || '',
            dhcpType:       device.dhcpType || 'dhcp_auth'
        });
        setIsEditOpen(true);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await deviceAPI.create({ ...formData, branchId: selectedBranch._id });
            toast.success('Device added');
            setIsAddOpen(false); setFormData(DEFAULT_FORM);
            refreshAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add device');
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await deviceAPI.update(editDevice._id, { ...formData, branchId: selectedBranch._id });
            toast.success('Device updated');
            setIsEditOpen(false); setEditDevice(null);
            refreshAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update device');
        }
    };

    /* ── Delete ────────────────────────────────────────────────────── */
    const handleDelete = async (id) => {
        if (!window.confirm('Remove this device?')) return;
        try {
            await deviceAPI.delete(id);
            toast.success('Device removed');
            setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
            refreshAll();
        } catch { toast.error('Failed to delete device'); }
    };

    /* ── Disconnect / Sync ──────────────────────────────────────────── */
    const handleDisconnect = async (device) => {
        setDisconnecting(p => ({ ...p, [device._id]: true }));
        try {
            await deviceAPI.disconnect(device._id);
            toast.success(`${device.name} disconnected`);
            fetchDevices();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to disconnect');
        } finally {
            setDisconnecting(p => ({ ...p, [device._id]: false }));
        }
    };

    const handleSync = async (device) => {
        setSyncing(p => ({ ...p, [device._id]: true }));
        try {
            const res = await deviceAPI.syncRadius(device._id);
            toast.success(res.data.message);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Sync failed');
        } finally {
            setSyncing(p => ({ ...p, [device._id]: false }));
        }
    };

    /* ── Selection helpers ─────────────────────────────────────────── */
    const toggleSelect = id => setSelectedIds(prev => {
        const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
    const toggleSelectAll = () => {
        if (selectedIds.size === devices.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(devices.map(d => d._id)));
    };

    /* ── Bulk actions ──────────────────────────────────────────────── */
    const execBulkAction = async (action, extra = {}) => {
        if (selectedIds.size === 0) return;
        const count = selectedIds.size;
        if (['delete','disconnect','blacklist'].includes(action)) {
            if (!window.confirm(`Apply "${action}" to ${count} device(s)?`)) return;
        }
        setBulkWorking(true);
        setShowBulkMenu(false);
        try {
            const res = await deviceAPI.bulkAction({ ids: [...selectedIds], action, ...extra });
            toast.success(res.data.message);
            setSelectedIds(new Set());
            refreshAll();
        } catch (err) {
            toast.error(err.response?.data?.message || `Bulk ${action} failed`);
        } finally {
            setBulkWorking(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedIds.size} device(s)?`)) return;
        setBulkWorking(true);
        try {
            await deviceAPI.bulkDelete([...selectedIds]);
            toast.success(`${selectedIds.size} devices deleted`);
            setSelectedIds(new Set()); refreshAll();
        } catch { toast.error('Bulk delete failed'); }
        finally { setBulkWorking(false); }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm('Delete ALL devices in this branch? This cannot be undone.')) return;
        setBulkWorking(true);
        try {
            const res = await deviceAPI.deleteAll({ branchId: selectedBranch._id });
            toast.success(res.data.message || 'All devices deleted');
            setSelectedIds(new Set()); refreshAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Delete all failed');
        } finally { setBulkWorking(false); }
    };

    /* ── Export ────────────────────────────────────────────────────── */
    const handleExport = () => {
        if (!devices.length) { toast.warning('No devices to export'); return; }
        const csv     = generateCSV(devices);
        const dateStr = new Date().toISOString().slice(0,10);
        const safe    = (selectedBranch?.name||'branch').replace(/\s+/g,'_');
        triggerDownload(csv, `devices_${safe}_${dateStr}.csv`);
        toast.success(`Exported ${devices.length} devices (current page)`);
    };

    /* ── Import ────────────────────────────────────────────────────── */
    const openImportModal = () => { setImportStep(1); setImportRows([]); setImportResult(null); setCsvError(''); setShowImportModal(true); };
    const handleCSVFile = (file) => {
        if (!file) return;
        if (!file.name.endsWith('.csv') && file.type !== 'text/csv') { setCsvError('Please upload a .csv file.'); return; }
        const reader = new FileReader();
        reader.onload = (e) => {
            const { rows, error } = parseCSV(e.target.result);
            if (error) { setCsvError(error); return; }
            if (!rows.length) { setCsvError('No data rows found.'); return; }
            setCsvError(''); setImportRows(rows); setImportStep(2);
        };
        reader.readAsText(file);
    };
    const handleImportSubmit = async () => {
        const validRows = importRows.filter(r => r._valid);
        if (!validRows.length) return;
        setImporting(true);
        try {
            const res = await deviceAPI.bulkImport({ devices: validRows, branchId: selectedBranch._id });
            setImportResult(res.data.data); setImportStep(3); refreshAll();
        } catch (err) { toast.error(err.response?.data?.message || 'Import failed'); }
        finally { setImporting(false); }
    };

    /* ── Discover ──────────────────────────────────────────────────── */
    const handleDiscover = async () => {
        if (!discoverNasId) return toast.warning('Select a router first');
        setDiscovering(true); setDiscovered(null);
        try {
            const res = await nasAPI.discoverDevices(discoverNasId);
            setDiscovered(res.data.data || []);
        } catch (err) { toast.error(err.response?.data?.message || 'Discovery failed'); setDiscovered([]); }
        finally { setDiscovering(false); }
    };

    /* ── Helpers ───────────────────────────────────────────────────── */
    const typeMeta = (type) => TYPE_OPTIONS.find(t => t.value === type) || TYPE_OPTIONS[TYPE_OPTIONS.length-1];

    const activeFilters = [filterStatus!=='all', filterSessionStatus!=='all', filterPlanId, filterBypass!=='all', filterExpired!=='all'].filter(Boolean).length;
    const clearFilters = () => { setFilterStatus('all'); setFilterSessionStatus('all'); setFilterPlanId(''); setFilterBypass('all'); setFilterExpired('all'); setSearchTerm(''); };

    // Blacklisting shows only where something is blacklisted — the Edit form still offers it.
    const blacklistUsed = (stats?.blacklisted || 0) > 0 || filterStatus === 'blacklisted';

    // The summary tiles; each one filters the list (again to show every device).
    const toggle = (current, value, set) => () => set(current === value ? 'all' : value);
    const tileFilters = [filterStatus, filterSessionStatus, filterBypass, filterExpired];
    const tiles = [
        { key: 'total', label: 'Total', val: stats?.total, icon: Laptop, tone: 'navy',
            active: tileFilters.every((f) => f === 'all'),
            onClick: () => { setFilterStatus('all'); setFilterSessionStatus('all'); setFilterBypass('all'); setFilterExpired('all'); } },
        { key: 'online', label: 'Online', val: stats?.online, icon: Activity, tone: 'green', active: filterSessionStatus === 'online', onClick: toggle(filterSessionStatus, 'online', setFilterSessionStatus) },
        { key: 'offline', label: 'Offline', val: stats?.offline, icon: Power, tone: 'gray', active: filterSessionStatus === 'offline', onClick: toggle(filterSessionStatus, 'offline', setFilterSessionStatus) },
        { key: 'whitelisted', label: 'Whitelisted', val: stats?.whitelisted, icon: Shield, tone: 'navy', active: filterStatus === 'whitelisted', onClick: toggle(filterStatus, 'whitelisted', setFilterStatus) },
        { key: 'bypass', label: 'Portal bypass', val: stats?.bypassEnabled, icon: Zap, tone: 'navy', active: filterBypass === 'true', onClick: toggle(filterBypass, 'true', setFilterBypass) },
        { key: 'expired', label: 'Expired', val: stats?.expired, icon: Clock, tone: 'amber', active: filterExpired === 'true', onClick: toggle(filterExpired, 'true', setFilterExpired) },
        ...((stats?.pending || 0) > 0 || filterStatus === 'pending'
            ? [{ key: 'pending', label: 'Pending', val: stats?.pending, icon: AlertCircle, tone: 'amber', active: filterStatus === 'pending', onClick: toggle(filterStatus, 'pending', setFilterStatus) }] : []),
        ...(blacklistUsed
            ? [{ key: 'blacklisted', label: 'Blacklisted', val: stats?.blacklisted, icon: Ban, tone: 'red', active: filterStatus === 'blacklisted', onClick: toggle(filterStatus, 'blacklisted', setFilterStatus) }] : []),
    ];


    /* ═══════════════════════════════════════════════════════════════
       RENDER
    ═══════════════════════════════════════════════════════════════ */
    return (
        <div className="devices-page">

            {/* ── Header ─────────────────────────────────────────── */}
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <Laptop className="title-icon" /> Device Management
                        </h1>
                        <p className="page-subtitle-modern">
                            MAC whitelist{blacklistUsed ? ' / blacklist' : ''} for TVs, cameras &amp; shared devices. PMS and voucher guests are managed separately.
                        </p>
                    </div>
                    {canManage && (
                        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                            <button onClick={() => setShowDiscoverModal(true)} className="btn-secondary-modern">
                                <Search size={16} /> Discover
                            </button>
                            <button onClick={handleExport} className="btn-secondary-modern">
                                <Download size={16} /> Export
                            </button>
                            <button onClick={openImportModal} className="btn-secondary-modern">
                                <Upload size={16} /> Import
                            </button>
                            <button onClick={() => openAdd()} className="btn-primary-modern">
                                <Plus size={18} /> Add Device
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Summary tiles — each filters the list ─────────────── */}
            <div className="dm-tiles">
                {tiles.map(({ key, label, val, icon: Icon, tone, active, onClick }) => (
                    <button key={key} type="button" className={`dm-tile${active ? ' active' : ''}`} aria-pressed={active} onClick={onClick}
                        title={key === 'total' ? 'Show every device' : `Show only: ${label.toLowerCase()}`}>
                        <span className={`dm-tile-icon dm-tone-${tone}`}><Icon size={18} /></span>
                        <span className="dm-tile-body">
                            <span className="dm-tile-value">{statsLoading && !stats ? '—' : (val ?? 0)}</span>
                            <span className="dm-tile-label">{label}</span>
                        </span>
                    </button>
                ))}
            </div>

            {/* ── Suggestions ────────────────────────────────────── */}
            {canManage && suggestions.length > 0 && (
                <div className="device-suggestions">
                    <h3><i className="fas fa-lightbulb"></i> Recently Seen, Not Yet Listed</h3>
                    <div className="suggestion-chips">
                        {suggestions.slice(0,10).map(s => (
                            <button key={s.mac} className="suggestion-chip" onClick={() => openAdd(s.mac)}
                                title={`Last seen ${timeAgo(s.lastSeen)} · ${s.lastIp || 'unknown IP'}`}>
                                <Plus size={12} /> {s.mac}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Search + Filter bar ─────────────────────────────── */}
            <div className="filters-section">
                <div className="search-box-modern" style={{ flex:1 }}>
                    <i className="fas fa-search"></i>
                    <input type="text" placeholder="Search MAC, name, IP, username…"
                        value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    {searchTerm && (
                        <button onClick={() => setSearchTerm('')} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af' }}>
                            <X size={14} />
                        </button>
                    )}
                </div>
                <button className={`btn-secondary-modern filter-toggle ${showFilters?'active':''}`}
                    onClick={() => setShowFilters(f => !f)}>
                    <Filter size={16} />
                    Filters {activeFilters > 0 && <span className="filter-badge">{activeFilters}</span>}
                </button>
                {activeFilters > 0 && (
                    <button className="btn-link-modern" onClick={clearFilters}>
                        <X size={13} /> Clear
                    </button>
                )}
            </div>

            {showFilters && (
                <div className="advanced-filters">
                    <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} aria-label="Access">
                        <option value="all">All Status</option>
                        <option value="whitelisted">Whitelisted</option>
                        {blacklistUsed && <option value="blacklisted">Blacklisted</option>}
                        <option value="pending">Pending</option>
                    </select>
                    <select className="filter-select" value={filterSessionStatus} onChange={e => setFilterSessionStatus(e.target.value)}>
                        <option value="all">All Sessions</option>
                        <option value="online">Online</option>
                        <option value="offline">Offline</option>
                    </select>
                    <select className="filter-select" value={filterPlanId} onChange={e => setFilterPlanId(e.target.value)}>
                        <option value="">All Plans</option>
                        {plans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                    </select>
                    <select className="filter-select" value={filterBypass} onChange={e => setFilterBypass(e.target.value)}>
                        <option value="all">All Bypass</option>
                        <option value="true">Bypass Enabled</option>
                        <option value="false">Bypass Disabled</option>
                    </select>
                    <select className="filter-select" value={filterExpired} onChange={e => setFilterExpired(e.target.value)}>
                        <option value="all">All Expiry</option>
                        <option value="false">Active Only</option>
                        <option value="true">Expired Only</option>
                    </select>
                </div>
            )}

            {/* ── Bulk action bar ─────────────────────────────────── */}
            {canDelete && devices.length > 0 && (
                <div className="bulk-action-bar">
                    <label className="select-all-label">
                        <input type="checkbox"
                            checked={selectedIds.size === devices.length && devices.length > 0}
                            onChange={toggleSelectAll} />
                        {selectedIds.size === devices.length && devices.length > 0 ? 'Deselect All' : `Select All (${devices.length})`}
                    </label>

                    {selectedIds.size > 0 && (
                        <>
                            <span className="selected-count">{selectedIds.size} selected</span>

                            {/* Bulk actions dropdown */}
                            <div className="bulk-dropdown-wrap" ref={bulkMenuRef}>
                                <button className="btn-secondary-modern btn-sm" onClick={() => setShowBulkMenu(m => !m)} disabled={bulkWorking}>
                                    <MoreVertical size={15} /> Actions
                                </button>
                                {showBulkMenu && (
                                    <div className="bulk-dropdown-menu">
                                        <button onClick={() => { setShowBulkMenu(false); setShowPlanModal(true); }}>
                                            <Zap size={14} /> Assign Plan
                                        </button>
                                        <button onClick={() => execBulkAction('enable-bypass')}>
                                            <Wifi size={14} /> Enable Portal Bypass
                                        </button>
                                        <button onClick={() => execBulkAction('disable-bypass')}>
                                            <Wifi size={14} /> Disable Portal Bypass
                                        </button>
                                        <button onClick={() => execBulkAction('whitelist')}>
                                            <Shield size={14} /> Set Whitelisted
                                        </button>
                                        {blacklistUsed && (
                                            <button onClick={() => execBulkAction('blacklist')}>
                                                <Ban size={14} /> Set Blacklisted
                                            </button>
                                        )}
                                        <button onClick={() => execBulkAction('disconnect')}>
                                            <Power size={14} /> Disconnect Sessions
                                        </button>
                                        <div className="bulk-menu-sep" />
                                        <button className="danger" onClick={handleBulkDelete}>
                                            <Trash2 size={14} /> Delete Selected
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    <button type="button" onClick={handleDeleteAll} disabled={bulkWorking}
                        className="dm-delete-all" title="Delete every device in this branch">
                        <Trash2 size={14} /> Delete all
                    </button>
                </div>
            )}

            {/* ── Device Table ─────────────────────────────────────── */}
            {devices.length === 0 && !loading ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><Laptop size={40} /></div>
                    <h3>{(debouncedSearch || activeFilters > 0) ? 'No devices match' : 'No devices yet'}</h3>
                    <p className="text-gray-600">
                        {(debouncedSearch || activeFilters > 0)
                            ? 'Nothing matches the search or filters.'
                            : 'Whitelist a TV, camera or shared device by its MAC address — or find the ones already on a router.'}
                    </p>
                    {(debouncedSearch || activeFilters > 0) ? (
                        <div className="dm-empty-actions">
                            <button type="button" className="btn-secondary-modern" onClick={clearFilters}><X size={14} /> Clear filters</button>
                        </div>
                    ) : canManage && (
                        <div className="dm-empty-actions">
                            <button type="button" className="btn-primary-modern" onClick={() => openAdd()}><Plus size={16} /> Add Device</button>
                            <button type="button" className="btn-secondary-modern" onClick={() => setShowDiscoverModal(true)}><Search size={16} /> Discover on a router</button>
                            <button type="button" className="btn-secondary-modern" onClick={openImportModal}><Upload size={16} /> Import CSV</button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="devices-table-wrap">
                    <table className="devices-table">
                        <thead>
                            <tr>
                                {canDelete && <th className="col-check"><input type="checkbox" onChange={e => e.target.checked ? setSelectedIds(new Set(devices.map(d=>d._id))) : setSelectedIds(new Set())} checked={selectedIds.size === devices.length && devices.length > 0} /></th>}
                                <th>Device</th>
                                <th>Status</th>
                                <th>Guest / Room</th>
                                <th>Plan</th>
                                <th>IP</th>
                                <th>NAS</th>
                                <th>Access</th>
                                <th>Last Login</th>
                                <th>Added</th>
                                {canManage && <th className="col-actions">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading
                                ? Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} className="dt-skeleton-row">
                                        {canDelete && <td><div className="dt-skel" style={{width:16,height:16}} /></td>}
                                        <td><div className="dt-skel" style={{width:'70%'}} /></td>
                                        <td><div className="dt-skel" style={{width:60}} /></td>
                                        <td><div className="dt-skel" style={{width:'60%'}} /></td>
                                        <td><div className="dt-skel" style={{width:'55%'}} /></td>
                                        <td><div className="dt-skel" style={{width:90}} /></td>
                                        <td><div className="dt-skel" style={{width:'50%'}} /></td>
                                        <td><div className="dt-skel" style={{width:70}} /></td>
                                        <td><div className="dt-skel" style={{width:60}} /></td>
                                        <td><div className="dt-skel" style={{width:70}} /></td>
                                        {canManage && <td><div className="dt-skel" style={{width:80}} /></td>}
                                    </tr>
                                ))
                                : devices.map(device => {
                                    const TypeIcon   = typeMeta(device.type).icon;
                                    const isSelected = selectedIds.has(device._id);
                                    const expired    = isExpired(device);
                                    const online     = device.sessionStatus === 'online';
                                    const guest      = device.assignedUserId;
                                    const roomNo     = guest?.pmsData?.roomNumber || guest?.roomNumber || null;
                                    const guestName  = guest?.name || null;
                                    const plan       = device.planId;
                                    const nas        = device.nasId;
                                    const dl         = plan?.bandwidth?.downloadSpeed;
                                    const ul         = plan?.bandwidth?.uploadSpeed;

                                    return (
                                        <tr key={device._id}
                                            className={`dt-row${isSelected ? ' dt-row-selected' : ''}${expired ? ' dt-row-expired' : ''}`}>

                                            {/* Checkbox */}
                                            {canDelete && (
                                                <td className="col-check">
                                                    <input type="checkbox" checked={isSelected}
                                                        onChange={() => toggleSelect(device._id)}
                                                        onClick={e => e.stopPropagation()} />
                                                </td>
                                            )}

                                            {/* Device name + MAC + icon */}
                                            <td>
                                                <div className="dt-device-cell">
                                                    <div className="dt-device-icon"><TypeIcon size={16} /></div>
                                                    <div>
                                                        <div className="dt-device-name">{device.name}</div>
                                                        <div className="dt-device-mac">{device.mac}</div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Online / Offline */}
                                            <td>
                                                <span className={`session-pill ${online ? 'online' : 'offline'}`}>
                                                    <span className="session-dot"></span>
                                                    {online ? 'Online' : 'Offline'}
                                                </span>
                                            </td>

                                            {/* Guest / Room */}
                                            <td>
                                                {(guestName || roomNo) ? (
                                                    <div className="dt-guest-cell">
                                                        {roomNo && <span className="dt-room-badge">Room {roomNo}</span>}
                                                        {guestName && <span className="dt-guest-name">{guestName}</span>}
                                                    </div>
                                                ) : <span className="dt-muted">—</span>}
                                            </td>

                                            {/* Plan + speed */}
                                            <td>
                                                {plan ? (
                                                    <div>
                                                        <div className="dt-plan-name">{plan.name}</div>
                                                        {(dl || ul) && <div className="dt-speed">↓{dl||'—'} / ↑{ul||'—'}</div>}
                                                    </div>
                                                ) : device.bypassPortal ? (
                                                    <span style={{ fontSize: '0.75rem', color: '#f59e0b', fontWeight: 600 }}>⚡ Unrestricted</span>
                                                ) : <span className="dt-muted">—</span>}
                                            </td>

                                            {/* IP + VLAN */}
                                            <td>
                                                {device.currentIp
                                                    ? <span className="dt-mono">{device.currentIp}{device.vlanId && <span className="vlan-chip" style={{marginLeft:4}}>V{device.vlanId}</span>}</span>
                                                    : <span className="dt-muted">—</span>}
                                            </td>

                                            {/* NAS */}
                                            <td>
                                                {nas
                                                    ? <span className="dt-nas">{nas.name || nas.nasIdentifier || nas.ipAddress}</span>
                                                    : <span className="dt-muted">—</span>}
                                            </td>

                                            {/* Tags */}
                                            <td>
                                                <div className="dt-tags">
                                                    <span className={`status-badge ${device.status}`}>
                                                        <span className="badge-dot"></span>
                                                        {device.status.charAt(0).toUpperCase()+device.status.slice(1)}
                                                    </span>
                                                    {device.bypassPortal && (
                                                        <span className="status-badge bypass-badge">
                                                            <Zap size={10} /> Bypass
                                                        </span>
                                                    )}
                                                    {expired && <span className="status-badge expired-badge">Expired</span>}
                                                </div>
                                            </td>

                                            {/* Last login */}
                                            <td>
                                                <span className="dt-time">{timeAgo(device.lastLoginAt)}</span>
                                                {device.expiryDate && (
                                                    <div className={`expiry-chip ${expired?'expired':'active'}`} style={{marginTop:2}}>
                                                        {expired ? 'Expired' : formatDate(device.expiryDate)}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Created */}
                                            <td><span className="dt-date">{formatDate(device.createdAt)}</span></td>

                                            {/* Actions */}
                                            {canManage && (
                                                <td className="col-actions">
                                                    <div className="footer-actions">
                                                        {plan && (
                                                            <button className="footer-btn sync-btn"
                                                                onClick={() => handleSync(device)}
                                                                disabled={syncing[device._id]}
                                                                title="Sync to RADIUS">
                                                                <RefreshCw size={13} className={syncing[device._id]?'spinning':''} />
                                                            </button>
                                                        )}
                                                        {online && (
                                                            <button className="footer-btn disconnect-btn"
                                                                onClick={() => handleDisconnect(device)}
                                                                disabled={disconnecting[device._id]}
                                                                title="Disconnect">
                                                                <Power size={13} className={disconnecting[device._id]?'spinning':''} />
                                                            </button>
                                                        )}
                                                        {canEdit && (
                                                            <button className="footer-btn edit-btn"
                                                                onClick={() => openEdit(device)}
                                                                title="Edit">
                                                                <Edit2 size={13} />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button className="delete-btn"
                                                                onClick={() => handleDelete(device._id)}
                                                                title="Delete">
                                                                <Trash2 size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })
                            }
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Pagination ──────────────────────────────────────── */}
            {totalPages > 1 && (
                <div className="pagination-bar">
                    <button className="pagination-btn" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                    <button className="pagination-btn" onClick={() => setPage(p => p-1)} disabled={page === 1}>
                        <ChevronLeft size={16} />
                    </button>
                    <span className="pagination-info">Page {page} of {totalPages} · {total} devices</span>
                    <button className="pagination-btn" onClick={() => setPage(p => p+1)} disabled={page === totalPages}>
                        <ChevronRight size={16} />
                    </button>
                    <button className="pagination-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
                </div>
            )}

            {/* ═══ ADD DEVICE MODAL ══════════════════════════════ */}
            {isAddOpen && (
                <div className="modal-overlay-modern" onClick={() => setIsAddOpen(false)}>
                    <div className="modal-modern modal-medium" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2><Plus size={18} style={{ marginRight:8, verticalAlign:'middle' }} />Add New Device</h2>
                            <button onClick={() => setIsAddOpen(false)} className="modal-close"><X size={20} /></button>
                        </div>
                        <DeviceForm onSubmit={handleCreate} submitLabel="Add Device"
                            formData={formData} setFormData={setFormData}
                            editDevice={editDevice} plans={plans} nasList={nasList}
                            onCancel={() => { setIsAddOpen(false); setIsEditOpen(false); }} />
                    </div>
                </div>
            )}

            {/* ═══ EDIT DEVICE MODAL ══════════════════════════════ */}
            {isEditOpen && editDevice && (
                <div className="modal-overlay-modern" onClick={() => setIsEditOpen(false)}>
                    <div className="modal-modern modal-medium" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2><Edit2 size={18} style={{ marginRight:8, verticalAlign:'middle' }} />Edit Device</h2>
                            <button onClick={() => setIsEditOpen(false)} className="modal-close"><X size={20} /></button>
                        </div>
                        <DeviceForm onSubmit={handleUpdate} submitLabel="Save Changes"
                            formData={formData} setFormData={setFormData}
                            editDevice={editDevice} plans={plans} nasList={nasList}
                            onCancel={() => { setIsAddOpen(false); setIsEditOpen(false); }} />
                    </div>
                </div>
            )}

            {/* ═══ BULK ASSIGN PLAN MODAL ════════════════════════ */}
            {showPlanModal && (
                <div className="modal-overlay-modern" onClick={() => setShowPlanModal(false)}>
                    <div className="modal-modern modal-small" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>Assign Plan to {selectedIds.size} Device(s)</h2>
                            <button onClick={() => setShowPlanModal(false)} className="modal-close"><X size={20} /></button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="form-group-modern full-width">
                                <label>Select Plan</label>
                                <select value={bulkPlanId} onChange={e => setBulkPlanId(e.target.value)}>
                                    <option value="">— Select —</option>
                                    {plans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowPlanModal(false)}>Cancel</button>
                            <button className="btn-primary-modern" disabled={!bulkPlanId}
                                onClick={() => { setShowPlanModal(false); execBulkAction('assign-plan', { planId: bulkPlanId }); setBulkPlanId(''); }}>
                                <Zap size={16} /> Assign Plan
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ BULK IMPORT MODAL ════════════════════════════ */}
            {showImportModal && (
                <div className="modal-overlay-modern" onClick={() => setShowImportModal(false)}>
                    <div className="modal-modern modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2><Upload size={18} style={{ marginRight:8, verticalAlign:'middle' }} />Bulk Import Devices</h2>
                                <div className="import-steps">
                                    {['Upload CSV','Preview','Result'].map((label, idx) => (
                                        <span key={idx} className={`import-step ${importStep===idx+1?'active':importStep>idx+1?'done':''}`}>
                                            <span className="import-step-num">{importStep>idx+1?'✓':idx+1}</span> {label}
                                            {idx < 2 && <span className="import-step-sep">›</span>}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <button onClick={() => setShowImportModal(false)} className="modal-close"><X size={20} /></button>
                        </div>
                        <div className="modal-body-modern">
                            {importStep === 1 && (
                                <div>
                                    <div className={`import-drop-zone ${dragOver?'drag-over':''}`}
                                        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={e => { e.preventDefault(); setDragOver(false); handleCSVFile(e.dataTransfer.files[0]); }}
                                        onClick={() => fileInputRef.current?.click()}>
                                        <Upload size={36} className="drop-icon" />
                                        <p className="drop-title">Drag &amp; drop your CSV here</p>
                                        <p className="drop-sub">or click to browse · .csv only</p>
                                        <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display:'none' }}
                                            onChange={e => handleCSVFile(e.target.files[0])} />
                                    </div>
                                    {csvError && <div className="import-error-box"><AlertCircle size={16} /> {csvError}</div>}
                                    <div className="import-format-info">
                                        <div className="import-format-header">
                                            <FileText size={15} />
                                            <strong>Required CSV Format</strong>
                                            <button className="btn-link-modern" onClick={() => triggerDownload(CSV_TEMPLATE, 'device_import_template.csv')}>
                                                <Download size={13} /> Template
                                            </button>
                                        </div>
                                        <div className="import-columns">
                                            {[
                                                { col:'mac',          req:true,  desc:'MAC address (AA:BB:CC:DD:EE:FF)' },
                                                { col:'name',         req:true,  desc:'Device label / nickname' },
                                                { col:'type',         req:false, desc:'tv · camera · speaker · printer · iot · laptop · mobile · tablet · other' },
                                                { col:'status',       req:false, desc:'whitelisted · blacklisted · pending (default: whitelisted)' },
                                                { col:'bypass_portal',req:false, desc:'true / false — skip captive portal' },
                                                { col:'plan',         req:false, desc:'Plan name (must match exactly)' },
                                                { col:'ip_address',   req:false, desc:'Reserved/static IP for a bypass device' },
                                                { col:'dhcp_type',    req:false, desc:'DHCP lease type, e.g. dhcp_auth' },
                                                { col:'notes',        req:false, desc:'Extra description' },
                                            ].map(c => (
                                                <div key={c.col} className="import-col-row">
                                                    <code className="import-col-name">{c.col}</code>
                                                    {c.req ? <span className="import-req">required</span> : <span className="import-opt">optional</span>}
                                                    <span className="import-col-desc">{c.desc}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {importStep === 2 && (
                                <div>
                                    <div className="import-preview-summary">
                                        <span className="preview-count valid"><CheckCircle size={14} /> {importRows.filter(r=>r._valid).length} valid</span>
                                        {importRows.filter(r=>!r._valid).length > 0 && (
                                            <span className="preview-count invalid"><AlertCircle size={14} /> {importRows.filter(r=>!r._valid).length} invalid</span>
                                        )}
                                        <button className="btn-link-modern" style={{ marginLeft:'auto' }} onClick={() => { setImportStep(1); setCsvError(''); }}>← Change file</button>
                                    </div>
                                    <div className="table-wrapper-modern import-preview-table">
                                        <table className="table-modern">
                                            <thead>
                                                <tr><th>#</th><th>MAC</th><th>Name</th><th>Type</th><th>Status</th><th>Bypass</th><th>Plan</th><th>Expiry</th><th>VLAN</th><th>NAS</th><th>Valid</th></tr>
                                            </thead>
                                            <tbody>
                                                {importRows.map((row, i) => (
                                                    <tr key={i} className={!row._valid?'import-row-invalid':''}>
                                                        <td>{row._row}</td>
                                                        <td><code>{row.mac||'—'}</code></td>
                                                        <td>{row.name||'—'}</td>
                                                        <td>{row.type}</td>
                                                        <td><span className={`status-badge ${row.status}`}><span className="badge-dot"></span>{row.status}</span></td>
                                                        <td>{row.bypassPortal?'Yes':'No'}</td>
                                                        <td>{row.planName||'—'}</td>
                                                        <td>{row.expiryDate||'—'}</td>
                                                        <td>{row.vlanId||'—'}</td>
                                                        <td>{row.nasName||'—'}</td>
                                                        <td>{row._valid ? <CheckCircle size={16} color="#10b981" /> : <span className="import-row-errors" title={row._errors.join(', ')}><AlertCircle size={16} color="#ef4444" /></span>}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {importStep === 3 && importResult && (
                                <div className="import-result">
                                    <div className="import-result-icon"><CheckCircle size={52} color="#10b981" /></div>
                                    <h3>Import Complete</h3>
                                    <div className="import-result-stats">
                                        <div className="import-stat-card green"><span className="import-stat-num">{importResult.added}</span><span className="import-stat-label">Devices Added</span></div>
                                        {importResult.updated > 0 && <div className="import-stat-card blue"><span className="import-stat-num">{importResult.updated}</span><span className="import-stat-label">Plans Updated</span></div>}
                                        <div className="import-stat-card orange"><span className="import-stat-num">{importResult.skipped}</span><span className="import-stat-label">Skipped</span></div>
                                        <div className="import-stat-card blue"><span className="import-stat-num">{importResult.total}</span><span className="import-stat-label">Total Rows</span></div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            {importStep === 1 && <button className="btn-secondary-modern" onClick={() => setShowImportModal(false)}>Cancel</button>}
                            {importStep === 2 && (
                                <>
                                    <button className="btn-secondary-modern" onClick={() => setShowImportModal(false)}>Cancel</button>
                                    <button className="btn-primary-modern"
                                        disabled={importing || importRows.filter(r=>r._valid).length===0}
                                        onClick={handleImportSubmit}>
                                        {importing ? <><span className="spinner-sm"></span> Importing…</> : <><Upload size={17} /> Import {importRows.filter(r=>r._valid).length} Devices</>}
                                    </button>
                                </>
                            )}
                            {importStep === 3 && <button className="btn-primary-modern" onClick={() => setShowImportModal(false)}>Done</button>}
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ DISCOVER MODAL ════════════════════════════════ */}
            {showDiscoverModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDiscoverModal(false)}>
                    <div className="modal-modern modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>Discover Devices from Router</h2>
                            <button onClick={() => setShowDiscoverModal(false)} className="modal-close"><X size={20} /></button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="form-group-modern full-width">
                                <label>Router</label>
                                <div style={{ display:'flex', gap:'0.5rem' }}>
                                    <select value={discoverNasId} onChange={e => setDiscoverNasId(e.target.value)} style={{ flex:1 }}>
                                        <option value="">Select a router</option>
                                        {nasList.map(n => <option key={n._id} value={n._id}>{n.name} ({n.ipAddress||'dynamic'})</option>)}
                                    </select>
                                    <button className="btn-primary-modern" onClick={handleDiscover} disabled={discovering}>
                                        {discovering ? 'Scanning…' : 'Scan'}
                                    </button>
                                </div>
                            </div>
                            {discovered !== null && (
                                discovered.length === 0
                                    ? <p className="text-gray-600">No devices found.</p>
                                    : (
                                        <div className="table-wrapper-modern">
                                            <table className="table-modern">
                                                <thead><tr><th>MAC</th><th>IP</th><th>Hostname</th><th>Source</th><th></th></tr></thead>
                                                <tbody>
                                                    {discovered.map(d => (
                                                        <tr key={d.mac}>
                                                            <td><code>{d.mac}</code></td>
                                                            <td>{d.ip||'—'}</td>
                                                            <td>{d.hostname||'—'}</td>
                                                            <td>{d.source}</td>
                                                            <td>
                                                                {d.alreadyListed
                                                                    ? <span className="status-badge inactive">{d.alreadyListed}</span>
                                                                    : <button className="btn-secondary-modern btn-sm" onClick={() => { setShowDiscoverModal(false); openAdd(d.mac); }}><Plus size={14} /> Add</button>}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowDiscoverModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Devices;
